/* Crafts theme.js - global boot + utilities */
(function () {
  'use strict';

  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const CraftsTheme = window.CraftsTheme = window.CraftsTheme || {};
  CraftsTheme.$ = $;
  CraftsTheme.$$ = $$;
  CraftsTheme.emit = (name, detail = {}) => document.dispatchEvent(new CustomEvent(name, { detail }));
  CraftsTheme.on = (name, fn) => document.addEventListener(name, fn);

  CraftsTheme.focusTrap = function (root, opts = {}) {
    const selector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    let lastFocused = opts.lastFocused || document.activeElement;
    function onKey(e) {
      if (e.key !== 'Tab') return;
      const focusables = $$(selector, root).filter(el => el.offsetParent !== null);
      if (focusables.length === 0) { e.preventDefault(); return; }
      const first = focusables[0];
      const last  = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
      else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
    }
    root.addEventListener('keydown', onKey);
    const focusables = $$(selector, root).filter(el => el.offsetParent !== null);
    (focusables[0] || root).focus();
    return function release() {
      root.removeEventListener('keydown', onKey);
      try { lastFocused && lastFocused.focus({ preventScroll: true }); } catch (e) {}
    };
  };

  CraftsTheme.lockBody   = () => document.body.classList.add('scroll-locked');
  CraftsTheme.unlockBody = () => document.body.classList.remove('scroll-locked');

  CraftsTheme.money = function (cents) {
    const value = (cents / 100).toFixed(2);
    const fmt = (window.moneyFormat || '${{amount}}').replace('{{amount}}', value).replace('{{ amount }}', value);
    return fmt;
  };

  CraftsTheme.fetchSections = async function (sections, url = '/') {
    const sep = url.indexOf('?') >= 0 ? '&' : '?';
    const target = url + sep + 'sections=' + encodeURIComponent(sections.join(','));
    const res = await fetch(target, { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error('Section render failed: ' + res.status);
    return res.json();
  };

  CraftsTheme.swapSection = function (name, html) {
    if (!html) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = html.trim();
    const newNode = tmp.firstElementChild;
    if (!newNode) return;
    const existing = document.getElementById(newNode.id) || document.querySelector('[data-section="' + name + '"]');
    if (existing && newNode) existing.replaceWith(newNode);
  };

  /* Mobile menu */
  function bindMobileMenu() {
    const drawer = $('[data-mobile-menu]');
    if (!drawer) return;
    const openers = $$('[data-mobile-menu-open]');
    const closers = $$('[data-mobile-menu-close]', drawer).concat($$('[data-mobile-menu-close]'));
    let release = null;
    function open() {
      drawer.setAttribute('data-state', 'open');
      drawer.setAttribute('aria-hidden', 'false');
      CraftsTheme.lockBody();
      release = CraftsTheme.focusTrap(drawer);
      document.addEventListener('keydown', onKey);
    }
    function close() {
      drawer.setAttribute('data-state', 'closed');
      drawer.setAttribute('aria-hidden', 'true');
      CraftsTheme.unlockBody();
      if (release) { release(); release = null; }
      document.removeEventListener('keydown', onKey);
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    openers.forEach(b => b.addEventListener('click', open));
    closers.forEach(b => b.addEventListener('click', close));
  }

  /* Accordion / details polish */
  function bindAccordions() {
    $$('details[data-accordion]').forEach(el => {
      el.addEventListener('toggle', () => {
        if (!el.open) return;
        $$('details[data-accordion]').forEach(other => { if (other !== el && other.hasAttribute('data-accordion-group') && other.getAttribute('data-accordion-group') === el.getAttribute('data-accordion-group')) other.open = false; });
      });
    });
  }

  function initPageToc() {
    const host = document.querySelector('[data-page-toc]');
    if (!host) return;
    const list = host.querySelector('[data-page-toc-list]');
    const article = host.querySelector('.page__prose');
    if (!list || !article) return;
    const headings = Array.from(article.querySelectorAll('h2'));
    if (!headings.length) { host.removeAttribute('data-page-toc'); list.parentElement.style.display = 'none'; return; }
    const slugify = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'section';
    const links = headings.map((h, i) => {
      if (!h.id) h.id = slugify(h.textContent) + '-' + i;
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent;
      li.appendChild(a);
      list.appendChild(li);
      return { h, a };
    });
    if ('IntersectionObserver' in window) {
      const obs = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          links.forEach(({ h, a }) => a.classList.toggle('is-active', h === e.target));
        });
      }, { rootMargin: '-30% 0px -60% 0px' });
      headings.forEach((h) => obs.observe(h));
    }
  }

  /* Announcement bar: when all items FIT inline (desktop) show them all with separators; only ROTATE
     one at a time when they don't fit (mobile). Re-evaluated on resize. Replaces the old behaviour
     where the bar just hid items 2/3 by breakpoint (so mobile showed only the first, nothing cycled). */
  function bindAnnouncement() {
    const bar = $('.announce');
    if (!bar) return;
    const items = $$('.announce__item', bar);
    if (items.length < 2) return;
    if (!document.getElementById('announce-rotate-css')) {
      const st = document.createElement('style');
      st.id = 'announce-rotate-css';
      st.textContent =
        /* fit mode: force every item visible inline + separators (override the breakpoint hides) */
        '.announce--showall .announce__item{display:block!important}' +
        '.announce--showall .announce__item::after{display:inline-block!important}' +
        /* rotate mode: one item at a time, faded */
        '.announce--rotate .announce__item{display:none!important}' +
        '.announce--rotate .announce__item.is-active{display:flex!important;align-items:center;justify-content:center;animation:announceFade .45s ease}' +
        '.announce--rotate .announce__item::after{display:none!important}' +
        '@keyframes announceFade{from{opacity:0}to{opacity:1}}';
      document.head.appendChild(st);
    }
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let timer = null, i = 0;
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    function fitsAll() {
      bar.classList.remove('announce--rotate');
      bar.classList.add('announce--showall');
      const prevJustify = bar.style.justifyContent;
      bar.style.justifyContent = 'flex-start';
      const fits = bar.scrollWidth <= bar.clientWidth + 1;
      bar.style.justifyContent = prevJustify;
      if (!fits) bar.classList.remove('announce--showall');
      return fits;
    }
    function apply() {
      stop();
      if (fitsAll()) {
        bar.classList.remove('announce--rotate');
        items.forEach((el) => el.classList.remove('is-active'));
      } else {
        bar.classList.remove('announce--showall');
        bar.classList.add('announce--rotate');
        i = 0;
        items.forEach((el, idx) => el.classList.toggle('is-active', idx === 0));
        if (!reduce) {
          timer = setInterval(() => {
            items[i].classList.remove('is-active');
            i = (i + 1) % items.length;
            items[i].classList.add('is-active');
          }, 4500);
        }
      }
    }
    apply();
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(apply); }
    let rt;
    window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(apply, 200); }, { passive: true });
  }

  // 2026-09-25: product-card colour swatches. Hover/tap previews that colour's photo on the card
  // and re-points the card link at that variant, so the next tap lands on the right colour.
  function bindCardSwatches() {
    document.addEventListener('click', (e) => {
      const sw = e.target.closest('[data-card-swatches] .cswatch');
      if (!sw) return;
      e.preventDefault();
      const card = sw.closest('.card');
      const link = card && card.querySelector('.card__link');
      const img = card && card.querySelector('.card__media img');
      const url = sw.getAttribute('data-swatch-url');
      const src = sw.getAttribute('data-swatch-image');
      if (img && src && img.getAttribute('src') !== src) {
        img.setAttribute('src', src);
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
      }
      if (link && url) link.setAttribute('href', url);
      sw.parentNode.querySelectorAll('.cswatch').forEach((s) => s.classList.remove('is-current'));
      sw.classList.add('is-current');
      const quick = card && card.querySelector('[data-quick-add]');
      if (quick && sw.getAttribute('data-swatch-variant')) quick.setAttribute('data-quick-add', sw.getAttribute('data-swatch-variant'));
    });
    document.addEventListener('pointerover', (e) => {
      const sw = e.target.closest('[data-card-swatches] .cswatch');
      if (!sw || e.pointerType === 'touch') return;
      const card = sw.closest('.card');
      const img = card && card.querySelector('.card__media img');
      const src = sw.getAttribute('data-swatch-image');
      if (img && src) {
        if (!img.dataset.origSrc) { img.dataset.origSrc = img.getAttribute('src') || ''; img.dataset.origSrcset = img.getAttribute('srcset') || ''; }
        img.setAttribute('src', src); img.removeAttribute('srcset'); img.removeAttribute('sizes');
      }
    });
    document.addEventListener('pointerout', (e) => {
      const sw = e.target.closest('[data-card-swatches] .cswatch');
      if (!sw || e.pointerType === 'touch') return;
      const card = sw.closest('.card');
      const img = card && card.querySelector('.card__media img');
      const current = sw.parentNode.querySelector('.cswatch.is-current');
      if (!img || !img.dataset.origSrc) return;
      if (current) { img.setAttribute('src', current.getAttribute('data-swatch-image') || img.dataset.origSrc); }
      else { img.setAttribute('src', img.dataset.origSrc); if (img.dataset.origSrcset) img.setAttribute('srcset', img.dataset.origSrcset); }
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    bindCardSwatches();
    bindMobileMenu();
    bindAccordions();
    initPageToc();
    bindAnnouncement();
  });
})();
